import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, LogOut, Users, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmt, fmtDate } from "@/lib/api";
import { Avatar } from "@/components/Header";
import { CopyField } from "@/components/CopyField";

const Stat = ({ label, value, testId }) => (
  <div className="bg-[var(--x-bg-deep)] rounded-lg p-4">
    <div className="text-[12px] font-semibold text-[var(--x-muted)]">{label}</div>
    <div data-testid={testId} className="text-lg font-extrabold tabular-nums mt-1">{value}</div>
  </div>
);

export default function Profile() {
  const { user, ready, setUser, signOut, refresh } = useAuth();
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/user/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => {
    if (user) load();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (ready && !user) return <Navigate to="/" replace />;
  if (!user) return null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/user/avatar", fd);
      setUser({ ...user, avatar_path: data.avatar_path });
      toast.success("Profil logosu güncellendi");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setUploading(false);
    e.target.value = "";
  };

  const claim = async () => {
    try {
      const { data } = await api.post("/user/affiliate/claim");
      toast.success(`₺${fmt(data.claimed)} bakiyenize aktarıldı`);
      load();
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const link = `${window.location.origin}/?ref=${user.ref_code}`;

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div className="x-panel p-5 sm:p-6 flex items-center gap-5 x-rise">
        <button data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()} className="relative group shrink-0">
          <Avatar user={user} size={84} />
          <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            {uploading ? <Loader2 className="animate-spin" /> : <Camera size={22} />}
          </div>
          <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[var(--x-blue)] flex items-center justify-center border-2 border-[var(--x-panel)]">
            <Camera size={13} />
          </div>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="avatar-file-input" />
        <div className="min-w-0 flex-1">
          <div data-testid="profile-username" className="text-2xl font-black truncate">{user.username}</div>
          <div className="text-[13px] text-[var(--x-muted)] font-semibold">Üyelik: {fmtDate(user.created_at)}</div>
          <div className="text-[13px] text-[var(--x-muted)] font-semibold mt-0.5">Bakiye: <b className="text-white">₺{fmt(user.balance)}</b></div>
        </div>
        <button data-testid="logout-btn" onClick={signOut} className="x-btn x-btn-secondary px-3 py-2.5" title="Çıkış Yap">
          <LogOut size={18} />
        </button>
      </div>

      <div className="x-panel p-5 sm:p-6 x-rise" style={{ animationDelay: ".06s" }}>
        <div className="font-extrabold mb-4">İstatistikler</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Toplam Bahis" value={stats?.bets ?? "-"} testId="stat-bets" />
          <Stat label="Kazanılan" value={stats?.wins ?? "-"} testId="stat-wins" />
          <Stat label="Çevrim" value={`₺${fmt(stats?.wagered)}`} testId="stat-wagered" />
          <Stat label="Ödeme" value={`₺${fmt(stats?.won)}`} testId="stat-won" />
        </div>
      </div>

      <div className="x-panel p-5 sm:p-6 x-rise" style={{ animationDelay: ".12s" }} data-testid="affiliate-section">
        <div className="flex items-center gap-2 font-extrabold"><Users size={18} className="text-[var(--x-blue)]" /> Ortaklık Programı</div>
        <p className="text-[13px] text-[var(--x-muted)] mt-1.5 mb-4">
          Linkinden kayıt olan oyuncuların net kaybının <b className="text-white">%{stats?.affiliate_percent ?? 20}</b>'si senin. Oyuncu kazandığında kazanç oranında komisyondan düşülür.
        </p>
        <CopyField label="Ortaklık Linkin" value={link} testId="affiliate-link" />
        <div className="grid grid-cols-3 gap-3 mt-4">
          <Stat label="Davet Edilen" value={stats?.referrals ?? 0} testId="affiliate-referrals" />
          <Stat label="Toplam Komisyon" value={`₺${fmt(stats?.affiliate_total)}`} testId="affiliate-total" />
          <Stat label="Çekilebilir" value={`₺${fmt(stats?.affiliate_balance)}`} testId="affiliate-balance" />
        </div>
        <button data-testid="affiliate-claim-btn" disabled={!stats || stats.affiliate_balance <= 0} onClick={claim} className="x-btn x-btn-primary w-full mt-4">
          Komisyonu Bakiyeye Aktar
        </button>
      </div>
    </div>
  );
}
